import React, { FC, Fragment, useCallback, useEffect, useState } from 'react';

import { SLASyncOption } from '@ariksa/notification/api';
import {
  Alert,
  AlertIcon,
  Box,
  Center,
  HStack,
  RadioGroup,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { entries, find, reduce, sortBy } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';
import { Dict } from 'types/utils';

import { Select } from 'app/components/DataEntry/Select';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { OutlinedIconButton, PrimaryButton } from 'components/DataEntry';
import {
  AutomationIcon,
  ExclamationTriangleIcon,
  TicketIcon,
} from 'components/Icons';
import { AvailableSLAsModal } from 'containers/Settings/Components/OrganizationConfig/AvailableSLAsModal';
import { OrgConfigBox } from 'containers/Settings/Components/OrganizationConfig/OrgConfigBox';
import {
  convertSlaMinutes,
  renderOrgSettingHeader,
  slaSyncOptions,
} from 'containers/Settings/Components/OrganizationConfig/utils';
import { selectSettings } from 'containers/Settings/selectors';
import { actions } from 'containers/Settings/slice';
import { renderRadioButton } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { toTitleCase } from 'utils/string';

export const SLASetting: FC = () => {
  const dispatch = useDispatch();

  const { updateSLAs, SLAs, availableSLAs } = useSelector(selectSettings);
  const [availableSlas, setAvailableSlas] = useState<Record<string, any>[]>([]);
  const [syncOption, setSyncOption] = useState<SLASyncOption>(
    SLASyncOption.NoSync,
  );
  const [isChanged, setIsChanged] = useState(false);
  const [slas, setSlas] = useState<Dict<number>>({});
  const [syncSlaSettingUpdated, setSyncSlaSettingUpdated] = useState(false);

  const slaModal = useDisclosure();

  //for each severity assigned sla value
  useEffect(() => {
    dispatch(
      actions.getSLAs({
        onSuccess: res =>
          setSlas(
            reduce(
              res,
              (acc, sla) => {
                acc[sla.severity] = sla.sla ?? 0;
                return acc;
              },
              {},
            ),
          ),
      }),
    );
  }, [dispatch]);

  const getAvailableSLAOptions = useCallback(() => {
    dispatch(
      actions.getAvailableSLAOptions({
        q: {},
        onSuccess: res => {
          if (res) {
            const slas = res.map(sla => ({
              label: sla.display_label,
              value: sla.minutes,
              data: sla,
            }));
            setAvailableSlas(sortBy(slas, 'value'));
          }
        },
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    getAvailableSLAOptions();
  }, [getAvailableSLAOptions]);

  const handleUpdateSLAs = () => {
    const payload = SLAs.data.reduce((acc, sla) => {
      return {
        ...acc,
        [`${sla.severity}_sla`]: sla.sla,
      };
    }, {});

    entries(slas).forEach(([key, value]) => {
      payload[`${key}_sla`] = value;
    });

    dispatch(
      actions.updateSLAs({
        q: {
          updateSlaRequest: { ...payload, sync_option: syncOption },
        },
        onSuccess: () => {
          setSyncSlaSettingUpdated(true);
          setIsChanged(false);
        },
      }),
    );
  };

  return (
    <Box>
      <OrgConfigBox>
        <Stack spacing={6}>
          {renderOrgSettingHeader({
            icon: <TicketIcon strokeWidth={10} stroke="currentColor" />,
            header: 'Apply SLAs to Issues and Tickets',
            subHeader:
              'Use SLAs to automatically set due dates for tickets created by Ariksa to resolve issues',
            color: customTheme.colors.blue[300],
            rightComponent: (
              <Box pr={10}>
                <OutlinedIconButton
                  leftIcon={<AutomationIcon w={6} h={6} />}
                  fontSize="md"
                  onClick={() => slaModal.onOpen()}
                >
                  Edit SLA
                </OutlinedIconButton>
              </Box>
            ),
          })}
          {SLAs.isLoading ? (
            <Center h="250px">
              <CustomSpinner />
            </Center>
          ) : (
            <Stack spacing={6} pl={12} ml={1}>
              <HStack spacing={2}>
                <Stack spacing={5} align={'start'} h={'full'} w="25%">
                  {map(slas, (value, severity) => {
                    return (
                      // Fixed: key each severity row (React missing-key warning).
                      <HStack key={severity}>
                        <Box w={'80px'} color={customTheme.colors.gray[300]}>
                          <Text>{toTitleCase(severity)}</Text>
                        </Box>
                        <Box w={'150px'}>
                          <Select
                            options={availableSlas}
                            value={
                              find(availableSlas, o => o.value === value) ?? {
                                label: convertSlaMinutes(value),
                                value: value ?? 0,
                              }
                            }
                            isLoading={availableSLAs.isLoading}
                            onChange={opt => {
                              setSlas({
                                ...slas,
                                [severity]: opt.value ?? 0,
                              });
                              setSyncSlaSettingUpdated(false);
                              setIsChanged(true);
                            }}
                            showClearIndicator={false}
                          />
                        </Box>
                      </HStack>
                    );
                  })}
                </Stack>
                <Box w="15%"></Box>
                <Stack w="60%" pr={10} spacing={4}>
                  {syncOption !== SLASyncOption.NoSync ? (
                    <Box
                      border="1px solid"
                      borderColor="#b4a786"
                      bg="#fcf1d3"
                      borderRadius={6}
                      px={4}
                      py={2}
                    >
                      <HStack fontSize="13px" spacing={4}>
                        <Center w={12} h={12} color="red">
                          <ExclamationTriangleIcon />
                        </Center>
                        <Text color="#69521e" lineHeight={1.3}>
                          This action will modify due dates for existing issues
                          and tickets. SLAs apply to date of issue detection to
                          calculate due date
                        </Text>
                      </HStack>
                    </Box>
                  ) : (
                    <Box h="66px" />
                  )}
                  <RadioGroup
                    value={syncOption}
                    onChange={val => {
                      setIsChanged(true);
                      setSyncOption(val as SLASyncOption);
                    }}
                  >
                    <Stack spacing={4}>
                      {/* Fixed: key each radio option (missing-key warning). */}
                      {map(slaSyncOptions, o => (
                        <Fragment key={o.key}>
                          {renderRadioButton(
                            o.label,
                            o.description,
                            o.key,
                            'black',
                          )}
                        </Fragment>
                      ))}
                    </Stack>
                  </RadioGroup>
                </Stack>
              </HStack>
              {updateSLAs.data && syncSlaSettingUpdated && (
                <Box w="full">
                  <Alert
                    status="info"
                    borderRadius={6}
                    //title={updateSLAs.data?.sync_results?.summary_message}
                  >
                    <AlertIcon />
                    {updateSLAs.data?.sync_results?.summary_message}
                  </Alert>
                </Box>
              )}
              <Box>
                <PrimaryButton
                  onClick={handleUpdateSLAs}
                  isLoading={updateSLAs.isLoading}
                  isDisabled={!isChanged}
                >
                  APPLY
                </PrimaryButton>
              </Box>
            </Stack>
          )}
        </Stack>
      </OrgConfigBox>
      {slaModal.isOpen && (
        <AvailableSLAsModal
          isOpen={slaModal.isOpen}
          onClose={slaModal.onClose}
          getAvailableSLAOptions={getAvailableSLAOptions}
          availableSlas={availableSlas}
        />
      )}
    </Box>
  );
};
